(() => {
  'use strict';

  const labels = Object.freeze({ 'side-panel': 'Side Panel', 'two-round-windows': 'Two Round Windows', 'center-window': 'Center Window', 'full-artwork': 'Full Artwork' });
  const copy = value => JSON.parse(JSON.stringify(value));
  const unavailable = () => new Error('The cassette placement preview is unavailable. Please reload before customizing.');
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const dimensions = image => ({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
  const transform = (value = {}) => ({ x: clamp(Number.isFinite(value.x) ? value.x : 0.5, 0, 1), y: clamp(Number.isFinite(value.y) ? value.y : 0.5, 0, 1), zoom: clamp(Number.isFinite(value.zoom) ? value.zoom : 1, 1, 8) });

  function insidePolygon(horizontal, vertical, polygon) {
    let inside = false;
    for (let current = 0, previous = polygon.length - 1; current < polygon.length; previous = current++) {
      const [currentX, currentY] = polygon[current];
      const [previousX, previousY] = polygon[previous];
      if ((currentY > vertical) !== (previousY > vertical) && horizontal < (previousX - currentX) * (vertical - currentY) / (previousY - currentY) + currentX) inside = !inside;
    }
    return inside;
  }

  function insideHole(horizontal, vertical, hole) {
    if (hole.type === 'circle') return (horizontal - hole.cx) ** 2 + (vertical - hole.cy) ** 2 <= hole.r ** 2;
    const closestX = clamp(horizontal, hole.x + hole.r, hole.x + hole.w - hole.r);
    return (horizontal - closestX) ** 2 + (vertical - hole.y - hole.r) ** 2 <= hole.r ** 2;
  }

  function coverRect(image, width, height, value) {
    const source = dimensions(image);
    const placement = transform(value);
    const scale = Math.max(width / source.width, height / source.height) * placement.zoom;
    const sourceWidth = width / scale;
    const sourceHeight = height / scale;
    return [(source.width - sourceWidth) * placement.x, (source.height - sourceHeight) * placement.y, sourceWidth, sourceHeight];
  }

  function validateGeometry(input) {
    if (!input || input.schema !== 2 || input.imageFit !== 'cover' || input.finalPrintTemplate !== false || input.reference?.width !== 2048 || input.reference?.height !== 2048 || input.reference?.sha256 !== 'df7a62d51310ad8541bbcc8f9d61d7e6897d3643efddaf128af8c4b4a6265737') throw unavailable();
    const source = new URL(input.reference.src);
    if (input.reference.src !== 'https://cdn.shopify.com/s/files/1/0783/9527/7411/files/taylor-swift-cassette-nfc-player.png?v=1791871895' || source.protocol !== 'https:' || source.hostname !== 'cdn.shopify.com' || source.username || source.password || source.port || source.hash) throw unavailable();
    if (!Array.isArray(input.layouts) || input.layouts.length !== 4 || new Set(input.layouts.map(layout => layout.id)).size !== 4) throw unavailable();
    input.layouts.forEach(layout => {
      if (labels[layout.id] !== layout.label || !Array.isArray(layout.slots) || layout.slots.length !== 9) throw unavailable();
      layout.slots.forEach((slot, index) => {
        const side = layout.id === 'side-panel';
        const expected = [902 + index % 3 * 394 + (side ? 216 : 0), 626 + Math.floor(index / 3) * 276, side ? 116 : 332, 161];
        if (slot.n !== index + 1 || JSON.stringify(slot.rect) !== JSON.stringify(expected) || !Array.isArray(slot.polygon) || slot.polygon.length < 3 || slot.polygon.some(point => point.length !== 2 || point.some(value => !Number.isFinite(value))) || !Array.isArray(slot.holes)) throw unavailable();
        const expectedHoles = layout.id === 'two-round-windows' ? 2 : layout.id === 'center-window' ? 1 : 0;
        if (slot.holes.length !== expectedHoles || slot.holes.some(hole => !['circle', 'capsule'].includes(hole.type) || !Number.isFinite(hole.r) || hole.r <= 0)) throw unavailable();
      });
    });
    return copy(input);
  }

  function create(input, baseImage, createCanvas = () => document.createElement('canvas')) {
    const geometry = validateGeometry(input);
    if (!baseImage || dimensions(baseImage).width !== geometry.reference.width || dimensions(baseImage).height !== geometry.reference.height) throw unavailable();
    const canvas = (width, height) => {
      const surface = createCanvas();
      surface.width = width;
      surface.height = height;
      const context = surface.getContext('2d');
      if (!context) throw unavailable();
      context.imageSmoothingQuality = 'high';
      context.imageSmoothingEnabled = false;
      return { surface, context };
    };
    const composite = canvas(2048, 2048);
    const card = canvas(332, 161);
    const content = canvas(332, 161);
    const maskCache = new Map();
    const layoutFor = identifier => {
      const layout = geometry.layouts.find(candidate => candidate.id === identifier);
      if (!layout) throw unavailable();
      return layout;
    };
    function maskFor(layout, index, kind = 'artwork') {
      const key = `${layout.id}:${index}:${kind}`;
      if (maskCache.has(key)) return maskCache.get(key);
      const slot = layout.slots[index];
      const [left, top, width, height] = slot.rect;
      const mask = canvas(width, height);
      const pixels = mask.context.createImageData(width, height);
      for (let row = 0; row < height; row += 1) {
        for (let column = 0; column < width; column += 1) {
          const horizontal = left + column + 0.5;
          const vertical = top + row + 0.5;
          const inside = insidePolygon(horizontal, vertical, slot.polygon);
          const hole = slot.holes.some(candidate => insideHole(horizontal, vertical, candidate));
          const donor = slot.reconstruction?.donor;
          const target = slot.reconstruction?.target;
          const included = kind === 'donor' ? donor && insideHole(horizontal, vertical, donor) : kind === 'target' ? target && insideHole(horizontal, vertical, target) : kind === 'hardware' ? inside && hole : inside && !hole;
          if (included) pixels.data.set([255, 255, 255, 255], (row * width + column) * 4);
        }
      }
      mask.context.putImageData(pixels, 0, 0);
      maskCache.set(key, mask.surface);
      return mask.surface;
    }
    function maskedLayer(mask, draw) {
      content.context.save();
      try {
        content.context.clearRect(0, 0, 332, 161);
        content.context.globalCompositeOperation = 'source-over';
        draw(content.context);
        content.context.imageSmoothingEnabled = false;
        content.context.globalCompositeOperation = 'destination-in';
        content.context.drawImage(mask, 0, 0);
      } finally {
        content.context.restore();
      }
      card.context.drawImage(content.surface, 0, 0);
    }
    function renderCard(layout, index, image, placement, includeGuides, confirmed) {
      const geometrySlot = layout.slots[index];
      const [left, top, width, height] = geometrySlot.rect;
      card.context.clearRect(0, 0, 332, 161);
      card.context.drawImage(baseImage, left, top, width, height, 0, 0, width, height);
      if (layout.id === 'center-window') {
        maskedLayer(maskFor(layout, index, 'hardware'), context => {
          context.fillStyle = geometry.hardwarePreview.bridgeColor;
          context.fillRect(0, 0, width, height);
        });
        maskedLayer(maskFor(layout, index, 'donor'), context => context.drawImage(baseImage, left, top, width, height, 0, 0, width, height));
      }
      if (geometrySlot.reconstruction) {
        const translation = geometrySlot.reconstruction.target.cx - geometrySlot.reconstruction.donor.cx;
        maskedLayer(maskFor(layout, index, 'target'), context => context.drawImage(baseImage, left - translation, top, width, height, 0, 0, width, height));
      }
      maskedLayer(maskFor(layout, index), context => {
        if (image) {
          context.imageSmoothingEnabled = true;
          context.drawImage(image, ...coverRect(image, width, height, placement), 0, 0, width, height);
        }
        else {
          context.fillStyle = '#e8e4de';
          context.fillRect(0, 0, width, height);
        }
        if (includeGuides && !confirmed) {
          context.fillStyle = '#302c28';
          context.fillRect(4, 4, 42, 27);
          context.fillStyle = '#ffffff';
          context.font = '600 18px sans-serif';
          context.textAlign = 'left';
          context.textBaseline = 'top';
          context.fillText(String(index + 1).padStart(2, '0'), 12, 8);
        }
      });
      return geometrySlot;
    }
    return Object.freeze({
      metadata(layoutId = 'side-panel') {
        const layout = layoutFor(layoutId);
        return { kind: geometry.kind, layout: layout.id, geometryId: geometry.id, coordinateSpace: 'reference-pixels', reference: copy(geometry.reference), imageFit: 'cover', transformSpace: 'normalized-overflow-of-approved-square', hardwareReconstructed: !!layout.hardwareReconstructionRequired, estimated: true, finalPrintTemplate: false };
      },
      paintCard(context, { layoutId = 'side-panel', slot, image, printTransform, index = 0, width = 664, height = 360 }) {
        if (!context || !Number.isInteger(index) || index < 0 || index > 8) throw unavailable();
        const layout = layoutFor(layoutId);
        const selectedImage = image || slot?.croppedImage || slot?.image;
        const geometrySlot = renderCard(layout, index, selectedImage, printTransform || slot?.printTransform, !image, !!slot?.confirmed);
        const [, , cardWidth, cardHeight] = geometrySlot.rect;
        const scale = Math.min((width - 24) / cardWidth, (height - 24) / cardHeight);
        context.clearRect(0, 0, width, height);
        context.fillStyle = '#f3eee7';
        context.fillRect(0, 0, width, height);
        context.drawImage(card.surface, 0, 0, cardWidth, cardHeight, (width - cardWidth * scale) / 2, (height - cardHeight * scale) / 2, cardWidth * scale, cardHeight * scale);
      },
      paint(context, { slots, includeGuides = false, outputSize = 1024, layoutId = 'side-panel' }) {
        if (!context || !Array.isArray(slots) || slots.length !== 9 || outputSize !== 1024) throw unavailable();
        const layout = layoutFor(layoutId);
        slots.forEach(slot => {
          if (slot?.confirmed && (!slot.croppedImage || dimensions(slot.croppedImage).width !== 1024 || dimensions(slot.croppedImage).height !== 1024)) throw unavailable();
          if (!includeGuides && !slot?.confirmed) throw new Error('Confirm all nine print compositions before exporting the cassette preview.');
        });
        composite.context.clearRect(0, 0, 2048, 2048);
        composite.context.drawImage(baseImage, 0, 0);
        layout.slots.forEach((geometrySlot, index) => {
          const slot = slots[index];
          renderCard(layout, index, slot?.croppedImage || slot?.image, slot?.printTransform, includeGuides, !!slot?.confirmed);
          const [left, top, width, height] = geometrySlot.rect;
          composite.context.drawImage(card.surface, 0, 0, width, height, left, top, width, height);
        });
        context.clearRect(0, 0, outputSize, outputSize);
        context.drawImage(composite.surface, 0, 0, outputSize, outputSize);
      }
    });
  }

  async function load(config, loadImage) {
    if (typeof config.cassetteGeometry !== 'string' || !config.cassetteGeometry || typeof loadImage !== 'function') throw unavailable();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(config.cassetteGeometry, { signal: controller.signal, credentials: 'omit' });
      if (!response.ok) throw unavailable();
      const geometry = validateGeometry(await response.json());
      if ('baseImage' in config && config.baseImage !== geometry.reference.src) throw unavailable();
      const baseImage = await loadImage(geometry.reference.src, true);
      return create(geometry, baseImage);
    } catch {
      throw unavailable();
    } finally {
      clearTimeout(timer);
    }
  }

  window.NFCCassettePreview = Object.freeze({ create, load, coverRect });
})();
